<template>
  <div class="alice-cs">
    <header class="alice-cs__head">
      <div class="alice-cs__head-main">
        <div class="alice-cs__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Conversation Starters</span>
        </div>
        <h1 class="alice-cs__title">
          Conversation Starters
          <span class="alice-cs__count">{{ items.length }}</span>
        </h1>
        <p class="alice-cs__subtitle">
          Curated prompts users can drop into the project chat and tweak before
          sending. Shared across all projects. Edit, then save.
        </p>
      </div>

      <button class="alice-cs__save" :disabled="saving" @click="save">
        <Icon name="plus" :size="16" />
        <span>{{ saving ? "Saving…" : "Save changes" }}</span>
      </button>
    </header>

    <div class="alice-cs__body">
      <div class="alice-cs__tabs" role="tablist">
        <button
          v-for="(s, i) in items"
          :key="s.id"
          type="button"
          class="alice-cs__tab"
          :class="{ 'alice-cs__tab--active': active === i }"
          role="tab"
          :aria-selected="active === i"
          @click="active = i"
        >
          <span>{{ s.title || "Untitled" }}</span>
        </button>
        <button
          type="button"
          class="alice-cs__tab alice-cs__tab--add"
          title="Add a new conversation starter"
          @click="addStarter"
        >
          <Icon name="plus" :size="14" />
          <span>Add starter</span>
        </button>
      </div>

      <div class="alice-cs__panel">
        <div v-if="!items.length" class="alice-cs__empty">
          No conversation starters yet. Click “Add starter” to create one.
        </div>

        <div v-else-if="current" class="alice-cs__editor">
          <div class="alice-cs__editor-meta">
            <span class="alice-cs__editor-label">Title</span>
          </div>
          <input
            v-model="current.title"
            class="alice-cs__title-input"
            type="text"
            placeholder="Short title (shown on the button)"
            spellcheck="false"
          />

          <div class="alice-cs__editor-meta alice-cs__editor-meta--text">
            <span class="alice-cs__editor-label">Text</span>
          </div>
          <textarea
            v-model="current.text"
            class="alice-cs__textarea"
            rows="20"
            placeholder="The full prompt inserted into the chat input."
            spellcheck="false"
          />

          <div class="alice-cs__editor-footer">
            <button
              type="button"
              class="alice-cs__delete"
              @click="removeStarter"
            >
              <Icon name="trash" :size="13" />
              <span>Delete starter</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/firebase";

import Icon from "@/components/alice/Icon.vue";

export default {
  name: "AdminConversationStarters",
  components: { Icon },
  data() {
    return {
      // Local working copy — edits aren't committed until "Save changes".
      items: [],
      active: 0,
      saving: false,
    };
  },
  computed: {
    ...mapState({
      conversationStarters: (state) => state.conversationStarters,
    }),
    current() {
      return this.items[this.active] || null;
    },
  },
  async mounted() {
    if (!this.conversationStarters || !this.conversationStarters.length) {
      await this.$store.dispatch("fetchConversationStarters");
    }
    this.items = (this.conversationStarters || []).map((s) => ({ ...s }));
  },
  methods: {
    addStarter() {
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `s-${Date.now()}-${this.items.length}`;
      this.items.push({ id, title: "", text: "" });
      this.active = this.items.length - 1;
    },
    removeStarter() {
      if (!this.items.length) return;
      const title = ((this.current && this.current.title) || "").trim();
      const label = title ? `"${title}"` : "this conversation starter";
      if (!confirm(`Delete ${label}? It will be removed when you save.`)) {
        return;
      }
      this.items.splice(this.active, 1);
      this.active = Math.max(0, Math.min(this.active, this.items.length - 1));
    },
    async save() {
      this.saving = true;
      this.$store.commit("setLoading", true);
      try {
        const items = this.items.map((s) => ({
          id: s.id,
          title: (s.title || "").trim(),
          text: s.text || "",
        }));
        await setDoc(doc(db, "admin", "conversationStarters"), { items });
        this.$store.commit("setConversationStarters", items);
        this.items = items.map((s) => ({ ...s }));
      } finally {
        this.saving = false;
        this.$store.commit("setLoading", false);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-cs {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-cs__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-cs__head-main {
  flex: 1;
  min-width: 0;
}
.alice-cs__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-cs__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-cs__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-cs__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 620px;
  line-height: 1.5;
}
.alice-cs__save {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-cs__save:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-cs__save:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Body ============== */
.alice-cs__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* ============== Tabs ============== */
.alice-cs__tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  padding: 0 32px;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
  overflow-x: auto;
}
.alice-cs__tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 18px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-3);
  white-space: nowrap;
  transition: color 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.alice-cs__tab:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-cs__tab--active,
.alice-cs__tab--active:hover {
  color: var(--vp-black);
  border-bottom-color: var(--accent-100);
  font-weight: 700;
  background: transparent;
}
.alice-cs__tab--add {
  color: var(--vp-black);
}

/* ============== Panel / editor ============== */
.alice-cs__panel {
  padding: 24px 32px 32px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.alice-cs__empty {
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-3);
  padding: 40px 0;
}
.alice-cs__editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-cs__editor-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.alice-cs__editor-meta--text {
  border-top: 1px solid var(--border-hairline);
}
.alice-cs__editor-label {
  color: var(--fg-3);
}
.alice-cs__title-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 14px 16px;
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 15px;
  font-weight: 600;
}
.alice-cs__title-input:focus {
  background: #fcfcfc;
}
.alice-cs__textarea {
  flex: 1;
  min-height: 320px;
  width: 100%;
  border: none;
  outline: none;
  resize: vertical;
  padding: 16px;
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.55;
  letter-spacing: 0;
}
.alice-cs__textarea:focus {
  background: #fcfcfc;
}

/* ============== Delete (subtle) ============== */
.alice-cs__editor-footer {
  display: flex;
  justify-content: flex-end;
  padding: 8px 10px;
  border-top: 1px solid var(--border-hairline);
}
.alice-cs__delete {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
  border-radius: 0;
  transition: color 0.12s ease;
}
.alice-cs__delete:hover {
  color: var(--vp-rood-100);
}

@media (max-width: 1279px) {
  .alice-cs__head {
    padding: 24px 20px 20px;
  }
  .alice-cs__tabs {
    padding: 0 20px;
  }
  .alice-cs__panel {
    padding: 20px;
  }
}
</style>
